'use client';

import { useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, ArrowUpRight, X } from '@phosphor-icons/react';
import {
  BagIcon,
  GiftIcon,
  InfoIcon,
  MapPinIcon,
  PhoneIcon,
  PlusIcon,
  QuestionIcon,
  SearchIcon,
  TruckIcon,
} from '@/components/icons';
import { siteConfig } from '@/config/site';
import { FAQ_CATEGORIES, type FAQCategory } from './faq-content';
import './faq-experience.css';

const normalize = (text: string) => text.toLocaleLowerCase('ru').replace(/ё/g, 'е').trim();
const topicIcons = {
  general: QuestionIcon,
  delivery: TruckIcon,
  promotions: GiftIcon,
  product: BagIcon,
  other: InfoIcon,
};
const topicDescriptions: Record<string, string> = {
  general: 'Оформление, оплата и всё, что нужно знать перед заказом.',
  delivery: 'Адрес, время, курьеры и помощь с доставленным заказом.',
  promotions: 'Подарки к заказу, промокоды и специальные предложения.',
  product: 'Состав, приготовление и ваши любимые роллы.',
  other: 'Ещё несколько полезных ответов.',
};
const answerCount = (count: number) => {
  const last = count % 10;
  const tens = count % 100;
  return (
    count +
    ' ' +
    (tens >= 11 && tens <= 14
      ? 'ответов'
      : last === 1
        ? 'ответ'
        : last >= 2 && last <= 4
          ? 'ответа'
          : 'ответов')
  );
};

function Highlight({ text, query }: { text: string; query: string }) {
  const search = normalize(query);
  if (!search) return <>{text}</>;
  const lower = text.toLocaleLowerCase('ru').replace(/ё/g, 'е');
  const parts = [];
  let cursor = 0;
  let match = lower.indexOf(search);
  while (match !== -1) {
    parts.push(text.slice(cursor, match));
    parts.push(<mark key={match}>{text.slice(match, match + search.length)}</mark>);
    cursor = match + search.length;
    match = lower.indexOf(search, cursor);
  }
  parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function AnswerText({ text, query }: { text: string; query: string }) {
  return text.split(/\n\s*\n/).map((paragraph, index) => {
    const lines = paragraph.split('\n');
    const bulletStart = lines.findIndex((line) => /^\s*[—–-]\s/.test(line));
    if (bulletStart !== -1) {
      return (
        <div key={index}>
          {bulletStart > 0 && (
            <p>
              <Highlight text={lines.slice(0, bulletStart).join(' ')} query={query} />
            </p>
          )}
          <ul>
            {lines.slice(bulletStart).map((line, item) => (
              <li key={item}>
                <Highlight text={line.replace(/^\s*[—–-]\s*/, '')} query={query} />
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return (
      <p key={index}>
        <Highlight text={paragraph} query={query} />
      </p>
    );
  });
}

export function FAQPage({ categories = FAQ_CATEGORIES }: { categories?: FAQCategory[] }) {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]?.id ?? '');
  const [expandedId, setExpandedId] = useState<string | null>(
    categories[0]?.questions[0]?.id ?? null
  );
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const questionRef = useRef<HTMLElement>(null);
  const search = normalize(query);
  const currentCategory =
    categories.find((category) => category.id === selectedCategory) ?? categories[0];
  const allQuestions = useMemo(
    () =>
      categories.flatMap((category) =>
        category.questions.map((question) => ({ ...question, categoryTitle: category.title }))
      ),
    [categories]
  );
  const questions = search
    ? allQuestions.filter((question) =>
        normalize(question.question + ' ' + question.answer).includes(search)
      )
    : (currentCategory?.questions ?? []).map((question) => ({
        ...question,
        categoryTitle: currentCategory?.title ?? '',
      }));
  const phoneHref = 'tel:' + siteConfig.phone.replace(/[^\d+]/g, '');
  const hours = siteConfig.workingHours.start + ':00–' + siteConfig.workingHours.end + ':00';
  const jumpToAnswers = () => {
    searchRef.current?.blur();
    questionRef.current?.focus({ preventScroll: true });
    questionRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
      block: 'start',
    });
  };
  const clearSearch = () => {
    setQuery('');
    searchRef.current?.focus();
  };

  return (
    <main className="faq-page" id="faq">
      <div className="faq-container">
        <section className="faq-hero" aria-labelledby="faq-title">
          <div className="faq-hero-copy">
            <Link className="faq-breadcrumb" href="/">
              Главная <span>/ Вопросы и ответы</span>
            </Link>
            <h1 id="faq-title">
              Вопросы
              <br />
              <span>и ответы</span>
            </h1>
            <p>
              О заказах, доставке и роллах.
              <br className="faq-desktop-break" /> Найдите ответ или позвоните нам.
            </p>
          </div>
          <div className="faq-hero-art" aria-hidden="true">
            <div className="faq-hero-arch" />
            <img
              src="/storefront/faq-mascot-phone-v1.webp"
              width={760}
              height={950}
              alt=""
              fetchPriority="high"
            />
            <span className="faq-hero-note">
              <PhoneIcon size={17} weight="fill" /> На связи
            </span>
          </div>
          <form
            className="faq-search-area"
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              jumpToAnswers();
            }}
          >
            <label className="faq-sr-only" htmlFor="faq-search">
              Поиск по вопросам и ответам
            </label>
            <div className="faq-search-field">
              <SearchIcon size={23} aria-hidden="true" />
              <input
                ref={searchRef}
                id="faq-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
                placeholder="Что хотите узнать?"
                aria-controls="faq-answers"
                enterKeyHint="search"
              />
              {query && (
                <button
                  className="faq-search-clear"
                  type="button"
                  onClick={clearSearch}
                  aria-label="Очистить поиск"
                >
                  <X size={19} />
                </button>
              )}
              <button className="faq-search-submit" type="submit" aria-label="Перейти к ответам">
                <ArrowRight size={22} />
              </button>
            </div>
            <div className="faq-search-hints">
              <span>Например</span>
              {['Доставка', 'Промокод', 'Оплата'].map((hint) => (
                <button
                  type="button"
                  key={hint}
                  onClick={() => {
                    setQuery(hint);
                    jumpToAnswers();
                  }}
                >
                  {hint}
                </button>
              ))}
            </div>
          </form>
        </section>

        <div className="faq-shortcuts">
          <Link className="faq-shortcut faq-shortcut-delivery" href="/delivery">
            <span className="faq-shortcut-icon">
              <MapPinIcon size={27} />
            </span>
            <span>
              <strong>Привезём по вашему адресу?</strong>
              <span>Карта, время и стоимость доставки</span>
            </span>
            <ArrowUpRight size={24} aria-hidden="true" />
          </Link>
          <Link className="faq-shortcut faq-shortcut-offers" href="/actions">
            <span>
              <span>К заказу есть кое-что ещё</span>
              <strong>Акции и подарки</strong>
            </span>
            <span className="faq-shortcut-arrow">
              <ArrowUpRight size={24} aria-hidden="true" />
            </span>
          </Link>
        </div>

        <section ref={questionRef} tabIndex={-1} className="faq-workspace" aria-label="Ответы на вопросы">
          <aside className="faq-topics">
            <div className="faq-topics-caption">
              <span>Темы вопросов</span>
              <span>{allQuestions.length}</span>
            </div>
            <nav className="faq-topic-list" aria-label="Темы вопросов">
              {categories.map((category) => {
                const Icon = topicIcons[category.id as keyof typeof topicIcons] ?? QuestionIcon;
                return (
                  <button
                    type="button"
                    key={category.id}
                    className="faq-topic"
                    aria-pressed={!search && currentCategory?.id === category.id}
                    onClick={(event) => {
                      setSelectedCategory(category.id);
                      setQuery('');
                      setExpandedId(null);
                      if (window.matchMedia('(max-width: 760px)').matches) {
                        const list = event.currentTarget.parentElement;
                        if (list)
                          list.scrollTo({
                            left: event.currentTarget.offsetLeft - list.offsetLeft - 16,
                            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                              ? 'instant'
                              : 'smooth',
                          });
                      }
                    }}
                  >
                    <Icon size={20} aria-hidden="true" />
                    <span>{category.title}</span>
                    <span className="faq-topic-count">{category.questions.length}</span>
                  </button>
                );
              })}
            </nav>
            <a href={phoneHref} className="faq-aside-phone">
              <PhoneIcon size={19} />
              <span>
                Нужна помощь с заказом?<strong>{siteConfig.phone}</strong>
              </span>
              <ArrowUpRight size={16} />
            </a>
          </aside>

          <div className="faq-answers" id="faq-answers">
            <div className="faq-answers-heading">
              <div>
                <h2>
                  {search ? 'Результаты поиска' : (currentCategory?.title ?? 'Вопросы и ответы')}
                </h2>
                <p>
                  {search
                    ? 'По запросу «' + query.trim() + '»'
                    : (topicDescriptions[currentCategory?.id ?? ''] ??
                      'Полезная информация о ваших заказах.')}
                </p>
              </div>
              <span
                className="faq-answer-count"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {answerCount(questions.length)}
              </span>
            </div>
            {questions.length ? (
              <div className="faq-question-list">
                {questions.map((question) => {
                  const open = expandedId === question.id;
                  return (
                    <article
                      className={'faq-question' + (open ? ' is-open' : '')}
                      key={question.id}
                    >
                      <h3>
                        <button
                          type="button"
                          className="faq-question-trigger"
                          id={'faq-question-' + question.id}
                          aria-expanded={open}
                          aria-controls={'faq-answer-' + question.id}
                          onClick={() => setExpandedId(open ? null : question.id)}
                        >
                          <span>
                            {search && <small>{question.categoryTitle}</small>}
                            <Highlight text={question.question} query={search} />
                          </span>
                          <span className="faq-question-toggle">
                            <PlusIcon size={19} aria-hidden="true" />
                          </span>
                        </button>
                      </h3>
                      <div
                        className="faq-answer"
                        id={'faq-answer-' + question.id}
                        role="region"
                        aria-labelledby={'faq-question-' + question.id}
                        hidden={!open}
                      >
                        <div className="faq-answer-text">
                          <AnswerText text={question.answer} query={search} />
                        </div>
                        {question.link && (
                          <Link href={question.link.href} className="faq-answer-link">
                            {question.link.label}
                            <ArrowUpRight size={17} />
                          </Link>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="faq-empty">
                <SearchIcon size={36} aria-hidden="true" />
                <h3>{search ? 'Такого ответа пока не нашли' : 'В этой теме пока нет вопросов'}</h3>
                <p>
                  {search
                    ? 'Попробуйте другое слово или позвоните нам — поможем с вашим вопросом.'
                    : 'Если нужна помощь с заказом, позвоните нам.'}
                </p>
                {search && (
                  <button type="button" onClick={clearSearch}>
                    Сбросить поиск <X size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="faq-contact" aria-labelledby="faq-contact-title">
          <div className="faq-contact-copy">
            <span className="faq-contact-eyebrow">
              <PhoneIcon size={16} /> Живой разговор
            </span>
            <h2 id="faq-contact-title">
              Лучше спросить
              <br />
              напрямую?
            </h2>
            <p>
              Если вопрос касается вашего заказа,
              <br className="faq-desktop-break" /> оператор поможет разобраться в деталях.
            </p>
          </div>
          <div className="faq-contact-action">
            <span>Каждый день · {hours}</span>
            <a href={phoneHref}>
              <span>{siteConfig.phone}</span>
              <ArrowUpRight size={28} />
            </a>
            <span className="faq-contact-note">Заказы оформляются на сайте и в приложении</span>
          </div>
        </section>
        <Link className="faq-menu-link" href="/#menu">
          С вопросами разобрались?{' '}
          <strong>
            Теперь к роллам <ArrowRight size={19} />
          </strong>
        </Link>
      </div>
    </main>
  );
}
